/**
 * Pack Station (§14, §18 /pack) — scanner-driven, keyboard-first, COMFORTABLE
 * density. A thin renderer over the PURE reducer in machine.ts (all decision
 * logic lives there; §2.3). Flow: scan slip → VERIFYING → WEIGHING → LABELLING
 * → DONE, plus mark-short, multi-box, park, manual-confirm, unpack, override.
 *
 * Peripherals are mockable (Task 2): in mock mode the scale is manual/simulated,
 * the camera is skipped, and the label/fulfil calls hit the mock backend —
 * completion is LOCAL-ONLY (no Shopify write). The real Web-Serial scale /
 * PrintNode / carrier / camera adapters slot in behind this exact flow later.
 */
import { useCallback, useEffect, useReducer, useRef, useState } from "react";
import type { OrderDTO, ShortageReasonCode } from "@cairn/shared";
import { SHORTAGE_REASON_CODES } from "@cairn/shared";
import {
  reduce,
  initialState,
  packableLines,
  matchScan,
  totalPacked,
  totalExpected,
  expectedBoxWeightG,
  readyToCompleteLabelFirst,
  type PackState,
  type SkuBarcodeAlias,
} from "./machine.js";
import { DEMO_ORDERS } from "./demoData.js";
import { api } from "../lib/api.js";
import { playCue } from "../lib/audio.js";
import { isMockMode } from "../lib/mockMode.js";
import { humanizeServiceCode, humanizeCode } from "../lib/format.js";
import { countryName } from "../lib/countries.js";
import { ScannerInput } from "../components/ScannerInput.js";
import { capturePackingPhoto } from "./capturePhoto.js";
import { Button, Badge, Icon, Thumb } from "../components/ui/index.js";

const ACTION_CODES = { COMPLETE_PACK: "##COMPLETE_PACK", BACK_TO_QUEUE: "##BACK_TO_QUEUE" } as const;

export function PackStation({
  stationId,
  autoOpenOrderId,
  onExit,
}: {
  stationId: string;
  /** Task 1 — open this order immediately on mount (quick-pack / scan-to-pack). */
  autoOpenOrderId?: string;
  onExit?: () => void;
}) {
  const [state, dispatch] = useReducer(reduce, undefined, () => initialState());
  const [busy, setBusy] = useState(false);
  const [shortFor, setShortFor] = useState<string | null>(null);
  // §Task3 — sku → product image, from the open response (packers visually confirm).
  const [lineImages, setLineImages] = useState<Record<string, string>>({});
  const videoRef = useRef<HTMLVideoElement>(null);
  const mock = isMockMode();

  useEffect(() => {
    if (state.cue !== "NONE") playCue(state.cue);
  }, [state.cue]);

  // Real camera only outside mock mode (best-effort; never blocks packing §13.4).
  useEffect(() => {
    if (mock || !navigator.mediaDevices?.getUserMedia) return;
    let stream: MediaStream | null = null;
    navigator.mediaDevices
      .getUserMedia({ video: { width: { ideal: 1920 }, height: { ideal: 1080 } } })
      .then((s) => {
        stream = s;
        if (videoRef.current) videoRef.current.srcObject = s;
      })
      .catch(() => undefined);
    return () => stream?.getTracks().forEach((t) => t.stop());
  }, [mock]);

  const openSlip = useCallback(
    async (code: string) => {
      setBusy(true);
      try {
        const resp = await api.openPack(code, stationId);
        const aliases: SkuBarcodeAlias[] = resp.aliases;
        setLineImages(resp.lineImages ?? {});
        // §15 — the open response carries the order's mode + (label-first) the
        // pre-printed label tracking the bench must confirm against.
        dispatch({
          type: "SCAN_SLIP",
          order: resp.order as unknown as OrderDTO,
          stationId,
          aliases,
          mode: resp.mode,
          expectedTracking: resp.expectedTracking ?? null,
        });
      } catch {
        dispatch({ type: "WRONG_ITEM", code, identified: null });
      } finally {
        setBusy(false);
      }
    },
    [stationId],
  );

  // Task 1 — quick-pack / scan-to-pack deep link: open the requested order once.
  const autoOpenedRef = useRef(false);
  useEffect(() => {
    if (autoOpenedRef.current || !autoOpenOrderId) return;
    autoOpenedRef.current = true;
    void openSlip(autoOpenOrderId);
  }, [autoOpenOrderId, openSlip]);

  const scanItem = useCallback(
    (code: string) => {
      const lineId = matchScan(state, code);
      if (lineId === null) dispatch({ type: "WRONG_ITEM", code, identified: null });
      else dispatch({ type: "SCAN_ITEM", code });
    },
    [state],
  );

  // Mock/real weigh: feed grams to the reducer (real-scale poll lives behind this).
  const weigh = useCallback(
    (grams: number) => dispatch({ type: "WEIGH", grams, boxIndex: state.currentBox }),
    [state.currentBox],
  );

  const onScan = useCallback(
    (code: string) => {
      if (busy) return;
      if (code === ACTION_CODES.BACK_TO_QUEUE) return void park("scanned BACK_TO_QUEUE");
      if (state.phase === "IDLE" || state.phase === "DONE") return void openSlip(code);
      if (state.phase === "VERIFYING") return scanItem(code);
      // §33.1 LABEL_FIRST — scanning the pre-printed label's barcode confirms it.
      if (state.phase === "MATCHING") return void dispatch({ type: "CONFIRM_LABEL", scan: code });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [busy, state.phase, openSlip, scanItem],
  );

  // On LABELLING: (mock) skip/auto photo → mock label → mock fulfil (local-only).
  useEffect(() => {
    if (state.phase !== "LABELLING" || !state.order) return;
    const order = state.order;
    void (async () => {
      if (!mock) {
        const blob = await capturePackingPhoto(videoRef.current);
        if (blob) await api.uploadPhoto(order.id, blob).catch(() => undefined);
      }
      dispatch({ type: "PHOTO_CAPTURED" });
      try {
        const label = await api.createLabel(order.id);
        dispatch({ type: "LABEL_OK", trackingNumber: label.trackingNumber });
        await api.fulfil(order.id).catch(() => undefined);
      } catch (err) {
        dispatch({ type: "LABEL_FAIL", error: err instanceof Error ? err.message : "label failed" });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.phase]);

  // On completion (DONE): mark the order's BatchItem PACKED so batch progress
  // updates (§40.4), and push fulfilment for LABEL_FIRST (PACK_FIRST already
  // pushed right after the bench label). Local-only; fires once per order.
  const completedRef = useRef<string | null>(null);
  useEffect(() => {
    if (state.phase !== "DONE" || !state.order) return;
    if (completedRef.current === state.order.id) return;
    completedRef.current = state.order.id;
    const id = state.order.id;
    void api.markPacked(id).catch(() => undefined);
    if (state.mode === "LABEL_FIRST") void api.fulfil(id).catch(() => undefined);
  }, [state.phase, state.order, state.mode]);

  const park = useCallback(
    async (reason: string) => {
      if (state.order) await api.park(state.order.id, reason).catch(() => undefined);
      dispatch({ type: "PARK", reason });
    },
    [state.order],
  );

  return (
    <div className="pack" data-density="comfortable" data-cue={state.cue}>
      <ScannerInput onScan={onScan} disabled={busy} />
      {!mock && <video ref={videoRef} autoPlay playsInline muted className="pack__camera" />}

      {state.phase === "IDLE" && (
        <IdleScreen stationId={stationId} message={state.message} mock={mock} onOpen={openSlip} onExit={onExit} />
      )}

      {state.order && state.phase !== "IDLE" && (
        <>
          <PackHeader order={state.order} mode={state.mode} packed={totalPacked(state)} expected={totalExpected(state)} />

          {(state.phase === "VERIFYING" || state.phase === "MATCHING" || state.phase === "WEIGHING") && (
            <div className="pack-main">
              <Checklist
                state={state}
                shortFor={shortFor}
                lineImages={lineImages}
                onScanHint={(code) => scanItem(code)}
                onManual={(lineId) => dispatch({ type: "MANUAL_CONFIRM", lineId, reason: "manual — no/damaged barcode" })}
                onUnpack={(lineId) => dispatch({ type: "UNPACK_ITEM", lineId })}
                onShortOpen={setShortFor}
                onShort={(lineId, foundQuantity, reasonCode) => {
                  dispatch({ type: "MARK_SHORT", lineId, foundQuantity, reasonCode });
                  setShortFor(null);
                }}
                onShortCancel={() => setShortFor(null)}
              />
              {state.phase === "MATCHING" ? (
                <MatchPanel state={state} mock={mock} onConfirm={(scan) => dispatch({ type: "CONFIRM_LABEL", scan })} />
              ) : (
                <WeighPanel
                  state={state}
                  mock={mock}
                  onWeigh={weigh}
                  onOverride={() => dispatch({ type: "WEIGHT_OVERRIDE", reason: "supervisor override" })}
                  onNewBox={() => dispatch({ type: "SET_BOX", boxIndex: state.boxes.length + 1, tareG: 0 })}
                  onComplete={() => dispatch({ type: "COMPLETE_PACK" })}
                />
              )}
            </div>
          )}

          {state.phase === "LABELLING" && (
            <Banner kind="info" icon="dot">Creating {state.order.carrierCode ?? "carrier"} label…</Banner>
          )}
          {state.phase === "LABEL_FAILED" && (
            <Banner kind="error" icon="alert">Label failed: {state.labelError}. Pack work is preserved — retry from the order.</Banner>
          )}
          {state.phase === "DONE" && <DoneScreen order={state.order} mode={state.mode} tracking={state.trackingNumber} mock={mock} />}
        </>
      )}

      {state.message && state.phase !== "IDLE" && state.phase !== "DONE" && (
        <div className={`pack-toast pack-toast--${state.cue.toLowerCase()}`} role="status">
          {state.message}
        </div>
      )}

      <footer className="pack-footer">
        <span className="pack-footer__station">
          <Icon name="box" /> {stationId}
        </span>
        <span className="pack-footer__phase">{phaseLabel(state.phase)}</span>
        {totalExpected(state) > 0 && (
          <span className="tnum">
            {totalPacked(state)}/{totalExpected(state)} items
          </span>
        )}
        <span className="pack-footer__spacer" />
        {state.order && state.phase !== "DONE" && (
          <Button variant="ghost" size="sm" icon="logout" onClick={() => void park("parked from bench")}>
            Park
          </Button>
        )}
      </footer>
    </div>
  );
}

function phaseLabel(p: PackState["phase"]): string {
  return { IDLE: "Idle", VERIFYING: "Verifying", MATCHING: "Match label", WEIGHING: "Weighing", LABELLING: "Labelling", DONE: "Done", LABEL_FAILED: "Label failed" }[p];
}

// ---------------------------------------------------------------------------

function IdleScreen({
  stationId,
  message,
  mock,
  onOpen,
  onExit,
}: {
  stationId: string;
  message: string | null;
  mock: boolean;
  onOpen: (code: string) => void;
  onExit?: () => void;
}) {
  return (
    <div className="pack-idle">
      <div className="pack-idle__hero">
        <Icon name="queue" size={40} />
        <h1>Scan a packing slip</h1>
        <p className="muted">Station {stationId}{onExit ? " · " : ""}{onExit && <button type="button" className="pack-link" onClick={onExit}>switch packer</button>}</p>
        {message && <p className="pack-idle__msg">{message}</p>}
      </div>
      {mock && (
        <div className="pack-picker">
          <p className="eyebrow">Demo orders — no scanner needed, pick one to pack</p>
          <div className="pack-picker__grid">
            {DEMO_ORDERS.map((o) => (
              <button key={o.id} type="button" className="pack-card" onClick={() => onOpen(o.id)}>
                <span className="pack-card__name mono">{o.name}</span>
                <span className="pack-card__lines">
                  {o.lines.filter((l) => l.shippable).length} line{o.lines.length === 1 ? "" : "s"} ·{" "}
                  {o.lines.reduce((s, l) => s + l.quantity, 0)} items
                </span>
                <span className="pack-card__meta">{humanizeServiceCode(o.serviceCode ?? "")} · {countryName(o.shippingCountry)}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function PackHeader({ order, mode, packed, expected }: { order: OrderDTO; mode: PackState["mode"]; packed: number; expected: number }) {
  return (
    <header className="pack-header">
      <span className="pack-header__name mono">{order.name}</span>
      <Badge>{order.storeRegion}</Badge>
      <Badge title={mode === "LABEL_FIRST" ? "Label printed up front; match it at the bench" : "Label created here at the bench"}>
        {mode === "LABEL_FIRST" ? "Label-first" : "Pack-first"}
      </Badge>
      <span className="pack-header__dest">
        {order.hasPickupPoint && order.pickupPoint ? (
          <>
            <Icon name="pin" /> {order.pickupPoint.name}
          </>
        ) : (
          countryName(order.shippingCountry)
        )}
      </span>
      <span className="pack-header__carrier">
        {order.carrierCode ?? "—"} · {humanizeServiceCode(order.serviceCode ?? "")}
      </span>
      <span className="pack-header__progress tnum">{packed}/{expected}</span>
    </header>
  );
}

function Checklist({
  state,
  shortFor,
  lineImages,
  onScanHint,
  onManual,
  onUnpack,
  onShortOpen,
  onShort,
  onShortCancel,
}: {
  state: PackState;
  shortFor: string | null;
  lineImages: Record<string, string>;
  onScanHint: (code: string) => void;
  onManual: (lineId: string) => void;
  onUnpack: (lineId: string) => void;
  onShortOpen: (lineId: string) => void;
  onShort: (lineId: string, found: number, reason: ShortageReasonCode) => void;
  onShortCancel: () => void;
}) {
  const order = state.order;
  if (!order) return null;
  const mock = isMockMode();
  const lines = packableLines(order);
  return (
    <ul className="pack-checklist">
      {lines.map((line) => {
        const lp = state.lines[line.id];
        const packedN = lp?.packed ?? 0;
        const required = lp?.foundQuantity ?? lp?.required ?? line.quantity;
        const complete = packedN >= required;
        const short = lp?.foundQuantity != null;
        return (
          <li key={line.id} className={"pack-line" + (complete ? " pack-line--done" : "") + (short ? " pack-line--short" : "")}>
            <span className="pack-line__check">
              {complete ? <Icon name="check" size={22} /> : <span className="pack-line__count tnum">{packedN}/{required}</span>}
            </span>
            <Thumb src={line.sku ? lineImages[line.sku] : undefined} alt={line.title} size={48} />
            <span className="pack-line__info">
              <span className="pack-line__title">{line.title}</span>
              <span className="pack-line__sub mono">
                {line.sku ?? "no SKU"}
                {line.barcode ? ` · ${line.barcode}` : " · no barcode"}
              </span>
              {line.properties && Object.keys(line.properties).length > 0 && (
                <span className="pack-line__props">
                  {Object.entries(line.properties).map(([k, v]) => (
                    <span key={k} className="tag tag--warn">{k}: {v}</span>
                  ))}
                </span>
              )}
            </span>
            <span className="pack-line__actions">
              {mock && !complete && line.barcode && (
                <Button size="sm" variant="secondary" onClick={() => onScanHint(line.barcode ?? "")}>Scan</Button>
              )}
              {!complete && (
                <Button size="sm" variant="secondary" onClick={() => onManual(line.id)}>Manual</Button>
              )}
              {packedN > 0 && (
                <Button size="sm" variant="ghost" onClick={() => onUnpack(line.id)}>Unpack</Button>
              )}
              {!short && (
                <Button size="sm" variant="ghost" onClick={() => onShortOpen(line.id)}>Short</Button>
              )}
            </span>
            {shortFor === line.id && (
              <ShortForm required={line.quantity} onConfirm={(found, reason) => onShort(line.id, found, reason)} onCancel={onShortCancel} />
            )}
          </li>
        );
      })}
    </ul>
  );
}

function ShortForm({ required, onConfirm, onCancel }: { required: number; onConfirm: (found: number, reason: ShortageReasonCode) => void; onCancel: () => void }) {
  const [found, setFound] = useState(Math.max(0, required - 1));
  const [reason, setReason] = useState<ShortageReasonCode>("OUT_OF_STOCK");
  return (
    <div className="pack-short" role="group" aria-label="Mark short">
      <label className="field">
        <span className="field__label">Found</span>
        <input className="control input" type="number" min={0} max={required} value={found} onChange={(e) => setFound(Math.max(0, Math.min(required, Number(e.target.value))))} />
      </label>
      <label className="field">
        <span className="field__label">Reason</span>
        <select className="control select" value={reason} onChange={(e) => setReason(e.target.value as ShortageReasonCode)}>
          {SHORTAGE_REASON_CODES.map((c) => (
            <option key={c} value={c}>{humanizeCode(c)}</option>
          ))}
        </select>
      </label>
      <Button size="sm" variant="danger" onClick={() => onConfirm(found, reason)}>Mark short</Button>
      <Button size="sm" variant="ghost" onClick={onCancel}>Cancel</Button>
    </div>
  );
}

function WeighPanel({
  state,
  mock,
  onWeigh,
  onOverride,
  onNewBox,
  onComplete,
}: {
  state: PackState;
  mock: boolean;
  onWeigh: (grams: number) => void;
  onOverride: () => void;
  onNewBox: () => void;
  /** §15 LABEL_FIRST — finish at the bench (no carrier call). */
  onComplete: () => void;
}) {
  const active = state.phase === "WEIGHING";
  const labelFirst = state.mode === "LABEL_FIRST";
  const box = state.boxes.find((b) => b.index === state.currentBox);
  const expected = state.order ? expectedBoxWeightG(state, state.currentBox) : 0;
  const [manual, setManual] = useState("");
  return (
    <aside className={"pack-weigh" + (active ? "" : " pack-weigh--disabled")}>
      <h2 className="eyebrow">
        {labelFirst ? "Weigh — discrepancy check" : "Weigh"} · box {state.currentBox}
      </h2>
      <p className="pack-weigh__expected">
        {labelFirst ? "Label printed for " : "Expected "}
        <strong className="tnum">{expected} g</strong> ±{state.tolerance.percent}%
      </p>
      {mock ? (
        <div className="pack-weigh__mock">
          <Button variant="primary" size="md" block disabled={!active} onClick={() => onWeigh(expected)}>
            Simulate stable reading (pass)
          </Button>
          <div className="pack-weigh__manual">
            <input
              className="control input"
              type="number"
              placeholder="grams"
              aria-label="Weight in grams"
              value={manual}
              disabled={!active}
              onChange={(e) => setManual(e.target.value)}
            />
            <Button variant="secondary" disabled={!active || !manual} onClick={() => onWeigh(Number(manual))}>
              Weigh
            </Button>
          </div>
          <Button variant="ghost" size="sm" disabled={!active} onClick={() => onWeigh(Math.round(expected * 1.3) + 80)}>
            Simulate {labelFirst ? "discrepancy" : "overweight (fail)"}
          </Button>
        </div>
      ) : (
        <Button variant="primary" block disabled={!active} onClick={() => onWeigh(expected)}>
          Place on scale
        </Button>
      )}

      {box?.weight && (
        <div
          role={box.weight.withinTolerance ? "status" : "alert"}
          className={box.weight.withinTolerance ? "pack-weigh__result pack-weigh__result--ok" : "pack-weigh__result pack-weigh__result--fail"}
        >
          {box.weight.withinTolerance ? <Icon name="check" /> : <Icon name="alert" />}
          Got {box.weight.actualG} g ({box.weight.deltaPercent.toFixed(1)}%)
        </div>
      )}

      {/* PACK_FIRST out-of-tolerance → supervisor override (it blocks). */}
      {!labelFirst && box?.weight && !box.weight.withinTolerance && (
        <Button variant="secondary" size="sm" block onClick={onOverride}>
          Override (supervisor)
        </Button>
      )}

      {/* LABEL_FIRST — the weigh never blocks; a discrepancy is logged and the
          packer completes (supervisor void+reprint is out of scope while mocked). */}
      {labelFirst && state.weightDiscrepancy && (
        <p className="pack-weigh__note">Discrepancy logged — void + reprint if the carrier bills on exact weight.</p>
      )}
      {labelFirst && (
        <Button variant="primary" block icon="check" disabled={!readyToCompleteLabelFirst(state)} onClick={onComplete}>
          Complete pack
        </Button>
      )}

      {/* Multi-box (§14.2): allowed while verifying too, so units can be scanned
          into a second box before weighing. */}
      <Button
        variant="ghost"
        size="sm"
        disabled={state.phase !== "VERIFYING" && state.phase !== "WEIGHING"}
        onClick={onNewBox}
      >
        + New box
      </Button>
    </aside>
  );
}

/** §33.1 LABEL_FIRST — confirm the order matches its pre-printed label before
 *  weighing. Scanning the label barcode (or the slip's order barcode) confirms;
 *  a mismatch is the primary label-first failure mode (wrong label on the box). */
function MatchPanel({
  state,
  mock,
  onConfirm,
}: {
  state: PackState;
  mock: boolean;
  onConfirm: (scan: string) => void;
}) {
  const mismatch = state.cue === "LABEL_MISMATCH";
  return (
    <aside className={"pack-weigh pack-match" + (mismatch ? " pack-match--mismatch" : "")}>
      <h2 className="eyebrow">Match pre-printed label</h2>
      <p className="pack-match__hint">
        Scan the barcode on this parcel’s pre-printed label to confirm it’s the right order.
      </p>
      {state.expectedTracking && (
        <p className="pack-weigh__expected">
          Expected tracking <strong className="mono">{state.expectedTracking}</strong>
        </p>
      )}
      {mismatch && (
        <div className="pack-weigh__result pack-weigh__result--fail" role="alert">
          <Icon name="alert" /> {state.message}
        </div>
      )}
      {mock && (
        <div className="pack-weigh__mock">
          <Button variant="primary" block icon="scan" onClick={() => onConfirm(state.expectedTracking ?? state.order?.id ?? "")}>
            Confirm label (scan)
          </Button>
          <Button variant="ghost" size="sm" onClick={() => onConfirm("WRONG-LABEL-9999")}>
            Simulate wrong label
          </Button>
        </div>
      )}
    </aside>
  );
}

function DoneScreen({ order, mode, tracking, mock }: { order: OrderDTO; mode: PackState["mode"]; tracking: string | null; mock: boolean }) {
  return (
    <div className="pack-done">
      <Icon name="check" size={56} />
      <h1>Packed {order.name}</h1>
      <p className="pack-done__carrier">
        {order.carrierCode ?? "—"}
        {mode === "LABEL_FIRST" ? " · pre-printed label matched" : " · label printed at bench"}
      </p>
      {tracking && <p className="pack-done__tracking mono">{tracking}</p>}
      {/* Task 1 — the packing slip auto-prints at pack (thermal/PrintNode mocked);
          this is the view/reprint of that same A4 document. */}
      <div className="pack-done__slip">
        <Icon name="printer" size={16} /> Packing slip printed
        <a className="pack-link" href={`/print/packing-slip/${order.id}`} target="_blank" rel="noreferrer">
          View / reprint
        </a>
      </div>
      {mock && <p className="pack-done__note">Demo — marked packed locally. No Shopify write (that's the later fulfilment leg).</p>}
      <p className="pack-done__cta">Scan the next slip to continue</p>
    </div>
  );
}

function Banner({ kind, icon, children }: { kind: "info" | "error"; icon: "dot" | "alert"; children: React.ReactNode }) {
  return (
    <div className={`pack-banner pack-banner--${kind}`} role={kind === "error" ? "alert" : "status"}>
      <Icon name={icon} /> {children}
    </div>
  );
}
